import React, { FormEvent } from 'react'
import { IMaskInput, IMask } from 'react-imask'
import Input from '@mui/material/Input'
import InputLabel from '@mui/material/InputLabel'
import FormControl from '@mui/material/FormControl'
import FormHelperText from '@mui/material/FormHelperText'
import { useTranslation, Trans } from 'react-i18next'
import useStyles from './styles'
import { useLanguage } from '@hooks/useLanguage'
import { useFormContext } from 'react-hook-form'
import { BookingFor } from '@customTypes/BookingFor'
import { ReactComponent as CakeImage } from 'src/images/cake.svg'
import { CustomMessageErrors } from '@hooks/useContactFormValidation'
import { useHistory } from 'react-router-dom'
import routes from 'src/config/routes'

interface CustomProps {
  onChange: (event: { target: { name: string; value: string } }) => void
  name: string
}

export const AGE_LIMIT = {
  [BookingFor.BOOKING_FOR_ME]: {
    MIN: 18,
    MAX: 120,
  },
  [BookingFor.BOOKING_FOR_OTHER]: {
    MIN: 1,
    MAX: 120,
  },
}

export const BIRTDATE_FORMAT_DATE_FNS = {
  UI: 'dd.MM.yyyy',
  PAYLOAD: 'yyyy-MM-dd',
}

export const BIRTHDATE_FORMAT = {
  en: 'DD.MM.YYYY',
  de: 'TT.MM.JJJJ',
}

export const DAY_CHAR = {
  en: 'D',
  de: 'T',
}

export const MONTH_CHAR = {
  en: 'M',
  de: 'M',
}

export const YEAR_CHAR = {
  en: 'Y',
  de: 'J',
}

const TextMaskCustom = React.forwardRef<HTMLInputElement, CustomProps>(function TextMaskCustom(props, ref) {
  const { onChange, ...other } = props
  const lang: 'en' | 'de' = useLanguage()
  const dayPlaceholder = DAY_CHAR[lang]
  const monthPlaceholder = MONTH_CHAR[lang]
  const yearPlaceholder = YEAR_CHAR[lang]

  return (
    <IMaskInput
      {...other}
      mask={Date}
      blocks={
        {
          d: { mask: IMask.MaskedRange, placeholderChar: dayPlaceholder, from: 1, to: 31, maxLength: 2 },
          m: { mask: IMask.MaskedRange, placeholderChar: monthPlaceholder, from: 1, to: 12, maxLength: 2 },
          Y: { mask: IMask.MaskedRange, placeholderChar: yearPlaceholder, from: 1900, to: 2999, maxLength: 4 },
        } as any
      }
      lazy={false}
      inputRef={ref}
      onAccept={(value: any) => onChange({ target: { name: props.name, value } })}
      overwrite
    />
  )
})

interface BirthdayProps {
  id: string
  onChange?: (e: FormEvent<HTMLInputElement | HTMLTextAreaElement>) => void
}

export default function Birthdate({ id, onChange }: BirthdayProps) {
  const { t } = useTranslation()
  const classes = useStyles()
  const { register, formState } = useFormContext()
  let errorMessage = formState.errors[id]?.message
  const { push } = useHistory()

  if (errorMessage === CustomMessageErrors.ChildProphylaxisAgeError) {
    errorMessage = (
      <Trans
        i18nKey="child_prophylaxis_age_error"
        components={[<span className={classes.selectDifferentTreatmentLink} onClick={() => push(routes.treatment)} />]}
      />
    )
  }

  const inputProps = register(id)

  return (
    <FormControl variant="standard" className={classes.root} fullWidth>
      <div className={classes.whiteBg}>
        <InputLabel className={classes.label} htmlFor={`${id}-input`}>
          {t('date_of_birth')}
        </InputLabel>
        <Input
          startAdornment={
            <div className={classes.startAdorement}>
              <CakeImage />
            </div>
          }
          className={classes.textField}
          fullWidth
          inputProps={{
            ...inputProps,
            onChange: (e) => {
              inputProps.onChange(e)
              onChange && onChange(e)
            },
            'data-test': `${id}-input`,
            className: 'birthdayInput',
          }}
          name="textmask"
          id={`${id}-input`}
          inputComponent={TextMaskCustom as any}
        />
      </div>
      {errorMessage && (
        <FormHelperText error id={`${id}-error-text`}>
          {errorMessage}
        </FormHelperText>
      )}
    </FormControl>
  )
}
